/**
 * RecapPanel — Post-wave explanation panel.
 * Shows a centered panel with title, icon+text items, and a confirm button.
 * Uses TechUI styling for consistency with the rest of the game.
 * Emits RECAP_CLOSED via EventBus when the player confirms.
 */
import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { TECH, fillTechRect, drawTechRect, createTechButton } from '@/ui/TechUI';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';
import { t } from '@/i18n/I18n';

const DEPTH_RECAP = 570;
const PANEL_W = 420;
const PAD = 20;
const ITEM_H = 28;

export interface RecapConfig {
  titleKey: string;
  items: Array<{ iconKey?: string; textKey: string }>;
  confirmKey: string;
}

export class RecapPanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;
  private dimBg: Phaser.GameObjects.Rectangle | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  show(config: RecapConfig): void {
    this.hide(true);

    const title = t(config.titleKey);
    const confirmLabel = t(config.confirmKey);
    const items = config.items.map(it => ({
      icon: it.iconKey ? t(it.iconKey) : '•',
      text: t(it.textKey),
    }));

    // ── Calculate panel height ──
    const titleH = 32;
    const itemsH = items.length * ITEM_H;
    const buttonH = 44;
    const panelH = PAD + titleH + 8 + itemsH + 16 + buttonH + PAD;

    // ── Dim background ──
    this.dimBg = this.scene.add.rectangle(
      CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2,
      CANVAS_WIDTH, CANVAS_HEIGHT,
      0x000000, 0.5,
    ).setDepth(DEPTH_RECAP - 1).setInteractive();

    // ── Container ──
    const cx = (CANVAS_WIDTH - PANEL_W) / 2;
    const cy = (CANVAS_HEIGHT - panelH) / 2;
    this.container = this.scene.add.container(cx, cy).setDepth(DEPTH_RECAP);

    // ── Panel background ──
    const gfx = this.scene.add.graphics();
    fillTechRect(gfx, 0, 0, PANEL_W, panelH, TECH.BG, 0.95);
    drawTechRect(gfx, 0, 0, PANEL_W, panelH, TECH.ACCENT, 1, 2);
    this.container.add(gfx);

    // ── Title ──
    const titleText = this.scene.add.text(PAD, PAD, title, {
      fontSize: '18px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    this.container.add(titleText);

    // ── Separator ──
    const sepGfx = this.scene.add.graphics();
    sepGfx.lineStyle(1, TECH.ACCENT, 0.3);
    sepGfx.lineBetween(PAD, PAD + titleH, PANEL_W - PAD, PAD + titleH);
    this.container.add(sepGfx);

    // ── Items ──
    let yOff = PAD + titleH + 8;
    for (const item of items) {
      const line = this.scene.add.text(PAD + 8, yOff, `${item.icon}  ${item.text}`, {
        fontSize: '13px', color: '#cccccc', fontFamily: 'monospace',
        wordWrap: { width: PANEL_W - PAD * 2 - 16 },
      });
      this.container.add(line);
      yOff += ITEM_H;
    }

    // ── Confirm button ──
    yOff += 12;
    const btn = createTechButton(
      this.scene,
      PANEL_W / 2, yOff + buttonH / 2,
      confirmLabel,
      {
        onClick: () => this.hide(),
        color: TECH.SUCCESS,
        width: PANEL_W - PAD * 4,
        height: buttonH,
        fontSize: '14px',
      },
    );
    this.container.add(btn);

    // ── Fade in ──
    this.container.setAlpha(0);
    this.dimBg.setAlpha(0);
    this.scene.tweens.add({
      targets: [this.container, this.dimBg],
      alpha: { from: 0, to: 1 },
      duration: 300,
      ease: 'Sine.easeOut',
    });
  }

  /** Hide the panel. Emits RECAP_CLOSED unless silent. */
  hide(silent = false): void {
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
    if (this.dimBg) {
      this.dimBg.destroy();
      this.dimBg = null;
    }
    if (!silent) {
      EventBus.emit(CinematicEvents.RECAP_CLOSED);
    }
  }

  destroy(): void {
    this.hide(true);
  }
}
